import { ChangeDetectionStrategy, Component, Input, ChangeDetectorRef, AfterViewChecked, OnChanges } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { catchError, take } from 'rxjs/operators';

import { State } from '@app/stp/state/reducers';
import { ButtonState } from '@global/infrastructure/interfaces/button-state';

import { SessionService } from '@global/services/session/session.service';
import { selectJourneyPath } from '@app/stp/state/selectors';
import { firstValueFrom } from 'rxjs';

import {
  CHANGE_DATE_ROUTE,
  CHANGE_TYPE_ROUTE,
  DASHBOARD_SELECT_ROUTE,
  ERRORS_SESSION_TIMEOUT,
  PAYMENT_DIRECT_DEBIT_EDIT_ROUTE,
  PREFERRED_PAYMENT_DATE_ROUTE,
  REVIEW_DIRECT_DEBIT_ROUTE,
} from '@app/infrastructure/constants';

@Component({
  selector: 'app-journey-navigation',
  templateUrl: './journey-navigation.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./journey-navigation.component.scss']
})
export class JourneyNavigationComponent implements AfterViewChecked, OnChanges {

  @Input() back: boolean;
  @Input() navigationDetails: ButtonState[];
  buttons: ButtonState[];
  backButton: ButtonState;
  hideBackground: boolean;
  showbackButton = true;

  constructor(
    private readonly store: Store<State>,
    private readonly cd: ChangeDetectorRef,
    private readonly sessionService: SessionService,
    private readonly router: Router
  ) { }

  onButtonClick(fn: (store?: any) => any, e) {
    if (fn) {
      fn(this.store);
    }

    if (['back', 'continue'].indexOf(e.target.id) > -1) {
      this.refreshSession();
    }
  }

  ngAfterViewChecked(): void {
    this.cd.detectChanges();
    this.hideBackground = [
      DASHBOARD_SELECT_ROUTE,
      CHANGE_TYPE_ROUTE,
      CHANGE_DATE_ROUTE,
      PAYMENT_DIRECT_DEBIT_EDIT_ROUTE,
      PREFERRED_PAYMENT_DATE_ROUTE,
      REVIEW_DIRECT_DEBIT_ROUTE,
    ].includes(this.router.url);
  }

  ngOnChanges(): void {
    this.buttons = this.getButtons();
    this.setButtonVisibilityMobile();
  }

  async setButtonVisibilityMobile() {
    const journeyPath = await firstValueFrom(this.store.select(selectJourneyPath));
    const isApp = localStorage.getItem('isApp');
    if (isApp === 'true' && this.router.url.includes('change-type')) {
      this.showbackButton = false;
    } else if (isApp === 'true' && (this.router.url.includes('premium') && journeyPath.path[0] === "premium")) {
      this.showbackButton = false;
    } else if (isApp === 'true' && 
      ((this.router.url.includes('direct-debit-edit') && journeyPath.path[0] === 'directDebitEdit') ||
      (this.router.url.includes('preferred-payment-date') && journeyPath.path[0] === 'prefferedPaymentDate'))) {
      this.showbackButton = false;
    } else {
      this.showbackButton = true;
    }
  }

  private refreshSession() {
    this.sessionService
      .getSession()
      .pipe(take(1))
      .subscribe((sessionId) => {
        this.sessionService
          .refreshSession(sessionId)
          .pipe(
            take(1),
            catchError(() => this.router.navigate([ERRORS_SESSION_TIMEOUT]))
          )
          .subscribe();
      });
  }

  private getButtons() {
    const buttons: ButtonState[] = [];

    if (this.navigationDetails) {
      if (this.back) {
        this.backButton = this.navigationDetails.filter(
          (btn) => btn.id === 'back'
        )[0];
      } else {
        this.navigationDetails = this.navigationDetails.filter(
          (btn) => btn.id !== 'back'
        );
      }

      this.navigationDetails.map((btn, i) => {
        switch (btn.id) {
          case 'continue':
            buttons.push({ ...btn, tabOrder: 1, visibleOrder: i });
            break;
          case 'back':
            buttons.push({ ...btn, tabOrder: 2, visibleOrder: i });
            break;
          case 'cancel':
            buttons.push({ ...btn, tabOrder: 3, visibleOrder: i });
            break;
          case 'skip':
            buttons.push({ ...btn, tabOrder: 4, visibleOrder: i });
            break;
          default:
            buttons.push({ ...btn, tabOrder: 5, visibleOrder: i });
            break;
        }
      });
      buttons.sort((a, b) => a.tabOrder - b.tabOrder);
    }

    return buttons;
  }

}
